import { Capacitor } from '@capacitor/core';
import { InAppBrowser, ToolbarPosition, iOSAnimation, iOSViewStyle } from '@capacitor/inappbrowser';

/**
 * Composable for handling external URL navigation
 * - On native with target='_blank': Opens URLs in device's external browser
 * - On native with target='_self': Opens URLs in InAppBrowser
 * - On web: Uses window.open() or window.location.href based on target
 */
export function useExternalUrl() {
  /**
   * Navigates to an external URL
   * @param url - The URL to navigate to (e.g., Stripe checkout or billing portal)
   * @param options - Optional configuration
   * @param options.target - Where to open the URL: '_blank' for external browser/new tab, '_self' for in-app/same window (default: '_self')
   */
  async function navigateToExternalUrl(
    url: string,
    options?: {
      target?: '_blank' | '_self';
    },
  ): Promise<void> {
    const target = options?.target || '_self';

    if (Capacitor.isNativePlatform()) {
      try {
        if (target === '_blank') {
          // Open in device's external browser (Safari, Chrome, etc.)
          await InAppBrowser.openInExternalBrowser({
            url,
          });
        } else {
          // Open in InAppBrowser (within the app)
          await InAppBrowser.openInWebView({
            url,
            options: {
              showURL: true,
              showToolbar: true,
              clearCache: false,
              clearSessionCache: false,
              mediaPlaybackRequiresUserAction: false,
              closeButtonText: 'Close',
              toolbarPosition: ToolbarPosition.TOP,
              showNavigationButtons: true,
              leftToRight: true,
              android: {
                allowZoom: true,
                hardwareBack: true,
                pauseMedia: true,
              },
              iOS: {
                allowOverScroll: true,
                enableViewportScale: true,
                allowInLineMediaPlayback: true,
                surpressIncrementalRendering: false,
                viewStyle: iOSViewStyle.FULL_SCREEN,
                animationEffect: iOSAnimation.FLIP_HORIZONTAL,
                allowsBackForwardNavigationGestures: true,
              },
            },
          });

          // Listen for browser close event (only for in-app browser)
          await InAppBrowser.addListener('browserClosed', () => {
            console.log('InAppBrowser closed');
            // You can trigger a refresh of subscription data here if needed
          });

          // Listen for page load event (only for in-app browser)
          await InAppBrowser.addListener('browserPageLoaded', () => {
            console.log('Page loaded in InAppBrowser');
          });
        }
      } catch (error) {
        console.error('Error opening browser:', error);
        // Fallback to standard navigation if InAppBrowser fails
        if (target === '_blank') {
          window.open(url, '_blank', 'noopener,noreferrer');
        } else {
          window.location.href = url;
        }
      }
    } else {
      // On web platform, respect the target option
      if (target === '_blank') {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = url;
      }
    }
  }

  /**
   * Closes the InAppBrowser (native only, only works for in-app web view)
   */
  async function closeInAppBrowser(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      try {
        await InAppBrowser.close();
      } catch (error) {
        console.error('Error closing InAppBrowser:', error);
      }
    }
  }

  /**
   * Removes all browser listeners (cleanup)
   */
  function removeListeners(): void {
    if (Capacitor.isNativePlatform()) {
      InAppBrowser.removeAllListeners();
    }
  }

  return {
    navigateToExternalUrl,
    closeInAppBrowser,
    removeListeners,
  };
}
